import { useState, useCallback } from 'react';

/**
 * Hook for NFC tag writing functionality
 * Uses the Web NFC API (Chrome on Android only)
 *
 * @returns {Object} NFC state and methods
 */
export function useNFC() {
  const [writing, setWriting] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);

  // Check if Web NFC API is supported
  const supported = typeof window !== 'undefined' && 'NDEFReader' in window;

  /**
   * Write a URL to an NFC tag
   * @param {string} url - The URL to write to the tag
   * @returns {Promise<boolean>} - True if successful
   */
  const writeTag = useCallback(async (url) => {
    if (!supported) {
      setError('NFC is not supported on this device/browser');
      return false;
    }

    setWriting(true);
    setError(null);
    setSuccess(false);

    try {
      // Create NFC reader/writer instance
      const ndef = new window.NDEFReader();

      // Write the URL as an NDEF URI record
      await ndef.write({
        records: [
          {
            recordType: 'url',
            data: url
          }
        ]
      });

      setSuccess(true);
      return true;
    } catch (err) {
      // Handle specific error types
      if (err.name === 'NotAllowedError') {
        setError('NFC permission was denied. Please allow NFC access and try again.');
      } else if (err.name === 'NotSupportedError') {
        setError('NFC is not supported on this device.');
      } else if (err.name === 'NotReadableError') {
        setError('Could not read the NFC tag. Please try again.');
      } else if (err.name === 'NetworkError') {
        setError('NFC tag is not compatible or is write-protected.');
      } else if (err.name === 'AbortError') {
        setError('NFC write was cancelled.');
      } else {
        setError(err.message || 'Failed to write to NFC tag');
      }
      return false;
    } finally {
      setWriting(false);
    }
  }, [supported]);

  /**
   * Reset the hook state
   */
  const reset = useCallback(() => {
    setWriting(false);
    setError(null);
    setSuccess(false);
  }, []);

  return {
    // State
    supported,
    writing,
    error,
    success,

    // Actions
    writeTag,
    reset
  };
}

export default useNFC;
